import CopilotIconAnimation from '@github-ui/copilot-chat/components/CopilotIconAnimation'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotIcon} from '@primer/octicons-react'
import {Flash, Heading} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useMemo, useRef} from 'react'

import styles from './CopilotCommitRecapPanel.module.css'

// Simple monogram component
const Monogram = ({initials}: {initials: string}) => <div className={styles.Box}>{initials.toUpperCase()}</div>

// Author avatar component that shows either GitHubAvatar or Monogram
const AuthorAvatar = ({
  login,
  avatarUrl,
  name,
  repoOwner,
  repoName,
  refName,
}: {
  login?: string
  avatarUrl?: string
  name: string
  repoOwner: string
  repoName: string
  refName?: string
}) => {
  const displayName = login || name

  // Generate initials from name (first character of first and last name, or just first two characters)
  const getInitials = (str: string) => {
    const words = str
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0)
    if (words.length >= 2) {
      return (words[0]?.charAt(0) || '') + (words[words.length - 1]?.charAt(0) || '')
    }
    return str.charAt(0) + (str.charAt(1) || '')
  }

  const initials = getInitials(displayName)

  if (avatarUrl && login) {
    // Create URL to commits page filtered by this author
    const commitsUrl = `/${encodeURIComponent(repoOwner)}/${encodeURIComponent(repoName)}/commits${
      refName ? `/${encodeURIComponent(refName)}` : ''
    }?author=${encodeURIComponent(login)}`

    return (
      <a href={commitsUrl} style={{textDecoration: 'none'}}>
        <GitHubAvatar src={avatarUrl} size={24} alt={`@${login}`} />
      </a>
    )
  }

  return <Monogram initials={initials} />
}

interface CopilotCommitRecapPanelProps {
  isVisible: boolean
  isLoading: boolean
  analysis: string
  error?: string | null
  repoOwner: string
  repoName: string
  refName?: string
  commitGroups?: Array<{
    type: string
    commits: Array<{
      author?: {
        login?: string
        avatar_url?: string
        name: string
        email: string
      }
    }>
  }>
}

const DEFAULT_COMMIT_GROUPS: CopilotCommitRecapPanelProps['commitGroups'] = []

export function CopilotCommitRecapPanel({
  isVisible,
  isLoading,
  analysis,
  error,
  repoOwner,
  repoName,
  refName,
  commitGroups = DEFAULT_COMMIT_GROUPS,
}: CopilotCommitRecapPanelProps) {
  const analysisBoxRef = useRef<HTMLDivElement>(null)

  // Create a map of author logins to their full information from commit groups
  const authorDataMap = useMemo(() => {
    const map = new Map<string, {login?: string; avatarUrl?: string; name: string}>()

    if (commitGroups) {
      for (const group of commitGroups) {
        for (const commit of group.commits) {
          if (commit.author) {
            const login = commit.author.login
            const name = commit.author.name
            const email = commit.author.email

            // Store by login if available
            if (login) {
              map.set(login, {
                login,
                avatarUrl: commit.author.avatar_url,
                name,
              })
            }

            // Store by name as well
            if (name && !map.has(name)) {
              map.set(name, {
                login,
                avatarUrl: commit.author.avatar_url,
                name,
              })
            }

            // Store by email prefix (before @) as another fallback
            if (email) {
              const emailPrefix = email.split('@')[0]
              if (emailPrefix && !map.has(emailPrefix)) {
                map.set(emailPrefix, {
                  login,
                  avatarUrl: commit.author.avatar_url,
                  name,
                })
              }
            }
          }
        }
      }
    }

    return map
  }, [commitGroups])

  const analysisSections = useMemo(() => {
    if (!analysis.trim()) return []

    return analysis
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Extract author mentions at the start of each line (e.g., "@author1 @author2: text" or "@John Doe: text")
        const authorMatch = line.match(/^(@[^\s:]+(?:\s+@[^\s:]+)*)\s*:\s*(.*)/)

        if (authorMatch) {
          const authorsText = authorMatch[1] || ''
          const content = authorMatch[2] || ''
          const authorLogins = authorsText.split(/\s+@/).map(author => author.replace('@', '').trim())

          const authors = authorLogins.map(loginOrName => {
            let authorData = authorDataMap.get(loginOrName)

            // If not found and it looks like a full name, try first name only
            if (!authorData && loginOrName.includes(' ')) {
              const firstName = loginOrName.split(' ')[0]
              if (firstName) {
                authorData = authorDataMap.get(firstName)
              }
            }

            return authorData || {login: undefined, name: loginOrName, avatarUrl: undefined}
          })

          return {
            authors,
            content: content.trim(),
            fullText: line,
          }
        }

        // Try to extract author names without @ symbols (e.g., "John Doe: text")
        const nameMatch = line.match(/^([A-Z][\w\s-]+?)\s*:\s*(.*)/)
        if (nameMatch) {
          const authorName = nameMatch[1]?.trim() || ''
          const content = nameMatch[2] || ''
          let authorData = authorDataMap.get(authorName)
          if (!authorData) {
            // If not found by full name, try first name only
            const firstName = authorName.split(' ')[0]
            if (firstName) {
              authorData = authorDataMap.get(firstName)
            }
          }
          if (!authorData) {
            authorData = {
              name: authorName,
              login: undefined,
              avatarUrl: undefined,
            }
          }

          return {
            authors: [authorData],
            content: content.trim(),
            fullText: line,
          }
        }

        return {
          authors: [],
          content: line,
          fullText: line,
        }
      })
  }, [analysis, authorDataMap])

  if (!isVisible) {
    return null
  }

  return (
    <div className={styles.Box_1}>
      <div className={styles.Box_2}>
        <Octicon icon={CopilotIcon} className={styles.Octicon} />
        <Heading as="h3" className={styles.Heading}>
          Commit Recap
        </Heading>
      </div>
      {isLoading && !analysis && (
        <div className={styles.Box_3}>
          <div className={styles.Box_4}>
            <CopilotIconAnimation />
          </div>
          <span className={styles.Text}>Analyzing recent commits...</span>
        </div>
      )}
      {error && (
        <Flash variant="danger" className={styles.Box_4}>
          {error}
        </Flash>
      )}
      {analysis && (
        <>
          <div ref={analysisBoxRef} className={styles.Box_5}>
            {analysisSections.map((section, index) => (
              <div key={section.fullText} className={styles.Box_6}>
                {/* Render author avatars/monograms */}
                {section.authors.length > 0 && (
                  <div className={styles.Box_7}>
                    {section.authors.slice(0, 3).map(author => (
                      <AuthorAvatar
                        key={author.login || author.name}
                        login={author.login}
                        avatarUrl={author.avatarUrl}
                        name={author.name}
                        repoOwner={repoOwner}
                        repoName={repoName}
                        refName={refName}
                      />
                    ))}
                    {section.authors.length > 3 && <span className={styles.Text_1}>+{section.authors.length - 3}</span>}
                  </div>
                )}

                {/* Render content */}
                <div className={styles.Box_8}>
                  <MarkdownRenderer
                    markdown={section.content}
                    isStreaming={isLoading && index === analysisSections.length - 1}
                    openLinksInCurrentTab={false}
                  />
                </div>
              </div>
            ))}
            {isLoading && (
              <div className={styles.Box_9}>
                <span className={styles.Text_1}>●</span>
              </div>
            )}
          </div>
          {!isLoading && (
            <span className={styles.Text_2}>
              AI-generated content may be inaccurate. Please verify important information.
            </span>
          )}
        </>
      )}
      {!isLoading && !error && !analysis && (
        <div className={styles.Box_10}>
          <span className={styles.Text_3}>Click &ldquo;History&rdquo; to get insights about recent commits</span>
        </div>
      )}
    </div>
  )
}

try{ Monogram.displayName ||= 'Monogram' } catch {}
try{ AuthorAvatar.displayName ||= 'AuthorAvatar' } catch {}
try{ CopilotCommitRecapPanel.displayName ||= 'CopilotCommitRecapPanel' } catch {}